import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { BookOpen, RefreshCw, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClinicalValueScore = ({ articleId, studyType }) => {
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (articleId) {
      loadClinicalValue();
    }
  }, [articleId]);

  const loadClinicalValue = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/articles/${articleId}/case-evaluation`);
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Evaluation not found - may still be processing');
        }
        throw new Error('Failed to load clinical value score');
      }
      const data = await response.json();
      setScore(data);
    } catch (err) {
      setError(err.message);
      console.error('Error loading clinical value:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-2 border-emerald-200">
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin text-emerald-600" />
            <span className="ml-2 text-slate-600">Loading clinical value...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return null; // Don't show anything if there's an error (not a case report)
  }

  if (!score) return null;

  const clinicalValue = score.clinical_learning_value || 0;
  const evidenceEquiv = score.evidence_equivalent || 0;
  const percentageComplete = (clinicalValue / 10) * 100;

  return (
    <Card className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-teal-50">
      <CardHeader className="border-b border-emerald-100">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl font-bold text-emerald-900 flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Clinical Learning Value
          </CardTitle>
          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
            📚 {studyType}
          </Badge>
        </div>
        <div className="mt-4 space-y-3">
          {/* Primary Score: 0-10 */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-600">Clinical Learning Value</span>
              <div className="text-right">
                <div className="text-3xl font-bold text-emerald-600">
                  {clinicalValue.toFixed(1)}
                </div>
                <div className="text-sm text-slate-600">
                  out of 10 points
                </div>
              </div>
            </div>
            <Progress 
              value={percentageComplete} 
              className="h-3 bg-slate-200"
            />
          </div>

          {/* Evidence Equivalent: 0-35 */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-blue-900">Evidence Score Equivalent</div>
                <div className="text-xs text-blue-700">For Impact Index calculation</div>
              </div>
              <div className="text-2xl font-bold text-blue-600">
                {evidenceEquiv.toFixed(1)}<span className="text-lg text-blue-500">/35</span>
              </div>
            </div>
            <div className="text-xs text-blue-600 mt-1">
              Formula: {clinicalValue.toFixed(1)} × 3.5 = {evidenceEquiv.toFixed(1)}
            </div>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-6">
        <div className="space-y-4">
          {/* AI Evaluation Explanation */}
          {score.evaluation_explanation && (
            <div className="p-4 rounded-lg border-2 bg-white border-emerald-200 shadow-sm">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">Overall Assessment</h4>
                  <p className="text-sm text-slate-700">{score.evaluation_explanation}</p>
                </div>
              </div>
            </div>
          )}

          {/* Component Breakdown */}
          <div className="space-y-3">
            {/* Documentation Quality */}
            <div className="p-4 rounded-lg border-2 bg-white border-emerald-200 shadow-sm">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">📋</span>
                  <div>
                    <h4 className="font-semibold text-slate-900">
                      Documentation Quality
                    </h4>
                    {score.component_justifications?.documentation_quality && (
                      <p className="text-xs text-slate-600 mt-1">
                        {score.component_justifications.documentation_quality}
                      </p>
                    )}
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-3">
                  <div className="text-lg font-bold text-emerald-600">
                    {(score.documentation_quality || 0).toFixed(1)}
                  </div>
                  <div className="text-xs text-slate-500">
                    / 3 pts
                  </div>
                </div>
              </div>
              <Progress 
                value={((score.documentation_quality || 0) / 3) * 100} 
                className="h-2 bg-emerald-100"
              />
            </div>

            {/* Novelty/Rarity */}
            <div className="p-4 rounded-lg border-2 bg-white border-emerald-200 shadow-sm">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">💎</span>
                  <div>
                    <h4 className="font-semibold text-slate-900">
                      Novelty / Rarity
                    </h4>
                    {score.component_justifications?.novelty_rarity && (
                      <p className="text-xs text-slate-600 mt-1">
                        {score.component_justifications.novelty_rarity}
                      </p>
                    )}
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-3">
                  <div className="text-lg font-bold text-emerald-600">
                    {(score.novelty_rarity || 0).toFixed(1)}
                  </div>
                  <div className="text-xs text-slate-500">
                    / 3 pts
                  </div>
                </div>
              </div>
              <Progress 
                value={((score.novelty_rarity || 0) / 3) * 100} 
                className="h-2 bg-emerald-100"
              />
            </div>

            {/* Educational Value */}
            <div className="p-4 rounded-lg border-2 bg-white border-emerald-200 shadow-sm">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🎓</span>
                  <div>
                    <h4 className="font-semibold text-slate-900">
                      Educational Value
                    </h4>
                    {score.component_justifications?.educational_value && (
                      <p className="text-xs text-slate-600 mt-1">
                        {score.component_justifications.educational_value}
                      </p>
                    )}
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-3">
                  <div className="text-lg font-bold text-emerald-600">
                    {(score.educational_value || 0).toFixed(1)}
                  </div>
                  <div className="text-xs text-slate-500">
                    / 2 pts
                  </div>
                </div>
              </div>
              <Progress 
                value={((score.educational_value || 0) / 2) * 100} 
                className="h-2 bg-emerald-100"
              />
            </div>

            {/* Follow-up/Outcome */}
            <div className="p-4 rounded-lg border-2 bg-white border-emerald-200 shadow-sm">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">📅</span>
                  <div>
                    <h4 className="font-semibold text-slate-900">
                      Follow-up & Outcome
                    </h4>
                    {score.component_justifications?.followup_outcome && (
                      <p className="text-xs text-slate-600 mt-1">
                        {score.component_justifications.followup_outcome}
                      </p>
                    )}
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-3">
                  <div className="text-lg font-bold text-emerald-600">
                    {(score.followup_outcome || 0).toFixed(1)}
                  </div>
                  <div className="text-xs text-slate-500">
                    / 2 pts
                  </div>
                </div>
              </div>
              <Progress 
                value={((score.followup_outcome || 0) / 2) * 100} 
                className="h-2 bg-emerald-100"
              />
            </div>
          </div>
        </div>
        
        {/* Footer Note */}
        <div className="mt-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
          <p className="text-xs text-emerald-800">
            <strong>📚 About Case Reports:</strong> Case reports and case series are essential for documenting rare conditions, 
            hypothesis generation, and clinical education. They are not designed to provide high-level statistical evidence, 
            but serve critical roles in medical discovery and learning. This score assesses educational value rather than 
            methodological rigor.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default ClinicalValueScore;
